import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultMST4060_R01HeaderDto, MST4060_R01HeaderDto } from './Dto_MST4060_R01';
import { useMST4060_R01Store } from './Store_MST4060_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'MST4060_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<MST4060_R01HeaderDto> | null>;
}

export const Table_MST4060_R01 = (props: Props) => {
	const { gridRef } = props;

	const {
		MST4060_R01List,
		selectedMST4060_R01,
		setSelectedMST4060_R01,
		initSelectedMST4060_R01,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useMST4060_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'equipmentCd',
		gridData: MST4060_R01List,
		defaultGridData: defaultMST4060_R01HeaderDto,
		selectedRow: selectedMST4060_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		equipmentCd: '부품코드',
		equipmentNm: '부품명',
		equipmentTypeNm: '부품유형',
		eqmQty: '보유량',
		eqmStatusNm: '상태',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'equipmentCd')(params), []);

	const baseColumnDefs: ColDef<MST4060_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '부품코드', colId: 'equipmentCd', field: 'equipmentCd', width: 120,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'equipmentCd'),
		},
		{
			headerName: '부품명', colId: 'equipmentNm', field: 'equipmentNm', width: 250,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'equipmentNm'),
		},
		{
			headerName: '부품유형', colId: 'equipmentTypeNm', field: 'equipmentTypeNm', width: 120,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'equipmentTypeNm'),
		},
		{
			headerName: '보유량', colId: 'eqmQty', field: 'eqmQty', width: 90,
			headerClass: aggridHeaderClass,
			valueFormatter: (p) => (p.value != null ? Number(p.value).toLocaleString() : '0'),
			cellStyle: (p) => ({ ...getCellStyle(p, 'eqmQty'), textAlign: 'right' }),
		},
		{
			headerName: '상태', colId: 'eqmStatusNm', field: 'eqmStatusNm', width: 90,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'eqmStatusNm'),
		},
	], [getCellStyle]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<MST4060_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<MST4060_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<MST4060_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedMST4060_R01?.equipmentCd === rowData.equipmentCd;
		setSelectedMST4060_R01(isSelected ? undefined : rowData);
		setDetailScrollTop(null);
	}, [selectedMST4060_R01, setSelectedMST4060_R01, setDetailScrollTop]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		equipmentCd: `${MST4060_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [MST4060_R01List.length]);

	useEffect(() => { ensureSelectedRow(); }, [MST4060_R01List, selectedMST4060_R01]);

	return (
		<GridContextWrapper<MST4060_R01HeaderDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedMST4060_R01}
			defaultRowData={defaultMST4060_R01HeaderDto}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<MST4060_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<MST4060_R01HeaderDto>({
					idKey: 'equipmentCd',
					selectedList: selectedMST4060_R01 ? [selectedMST4060_R01.equipmentCd] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
